import { type RefObject, useCallback } from "react";
import { type ChartExportMetadata, type ExportType, exportChart } from "@/utils/chartExport";
import type { BaseDataPoint } from "@/utils/charts";

export interface UseChartExportOptions {
  datasets: BaseDataPoint[][] | BaseDataPoint[];
  metadata?: ChartExportMetadata;
  svgRef: RefObject<SVGSVGElement | null>;
}

/**
 * Hook for exporting chart data in various formats (CSV, SVG, PNG).
 * Provides a stable callback for triggering exports with memoized dependencies.
 *
 * @param options - Configuration for chart export
 * @param options.datasets - Chart data as single array or array of arrays for multi-series
 * @param options.metadata - Export metadata including labels and filename options
 * @param options.svgRef - Reference to the chart's SVG element
 * @returns Async function to trigger export for a given format
 *
 * @example
 * ```tsx
 * const svgRef = useRef<SVGSVGElement>(null);
 * const handleExport = useChartExport({
 *   datasets: chartData,
 *   metadata: {
 *     xLabel: 'Date',
 *     yLabel: 'Value',
 *     seriesLabels: ['Series 1', 'Series 2'],
 *     filename: 'sales-report',
 *     timestamp: true
 *   },
 *   svgRef
 * });
 *
 * // In your component
 * <Button onPress={() => handleExport('csv')}>Export CSV</Button>
 * <Button onPress={() => handleExport('png')}>Export PNG</Button>
 * ```
 */
export function useChartExport({
  datasets,
  metadata,
  svgRef,
}: UseChartExportOptions): (type: ExportType) => Promise<void> {
  return useCallback(
    async (type: ExportType) => {
      try {
        await exportChart(type, datasets, metadata, svgRef);
      } catch (error: unknown) {
        console.error(`Chart export failed:`, error);
        throw error; // Re-throw for component-level error handling
      }
    },
    [datasets, metadata, svgRef]
  );
}
